<template>
  <!--
    P3 (student-foundation-redesign) -- near-final rich student Home,
    replacing MinimalHomeWelcome (TASK-SF-11). Visual composition matches the
    rendered prototype directly (00_*.md §12 PF-09; 02_*.md §12), not the
    prototype's source/JSX -- the availability banner stays omitted (no real
    data source backs it) and Reports is a subordinate entry, never occupying
    the Teacher/Advisor primary row (PF-11.5).

    Real data only: firstName from the user store. No fabricated stats, no
    Wallet/Credit, no notification UI, no availability claim.

    V405-F1 (doc 26 §12, F1-2, human decision 2026-09-29): the
    profile-completion promo card is gone from Home, and with it the profile
    read that existed only to decide it. Nothing replaces it; Profile stays
    reachable through the Student navigation.
  -->
  <div class="student-home-rich" data-testid="student-home-rich">
    <section class="student-home-rich__welcome">
      <h1>
        {{ $t('studentHome.header.greeting') }}
        <template v-if="firstName">{{ firstName }}</template>
        <span aria-hidden="true">👋</span>
        {{ $t('studentHome.header.tagline') }}
      </h1>
      <p>{{ $t('studentHome.header.subline') }}</p>
    </section>

    <ai-tutor-hero />

    <section
      class="student-home-rich__grid"
      aria-labelledby="student-home-help-heading"
    >
      <div class="student-home-rich__grid-heading">
        <h2 id="student-home-help-heading">
          {{ $t('studentHome.helpSection.heading') }}
        </h2>
        <p>{{ $t('studentHome.helpSection.subline') }}</p>
      </div>
      <div class="student-home-rich__grid-cards">
        <!--
          SX-02 (doc 22 §5.1): while the Student teacher capability is OFF the
          Teacher card keeps its place and geometry but is a badged button
          that raises the shared Coming Soon dialog; with the capability ON
          it is the accepted P2 link, unchanged.
        -->
        <home-action-card
          testid="home-card-human-help"
          icon="teacher"
          :to="
            teacherExperienceAvailable ? `/${$i18n.locale}/help/request` : null
          "
          :title="$t('studentHome.humanHelp.title')"
          :sub="$t('studentHome.humanHelp.sub')"
          :action-text="
            teacherExperienceAvailable
              ? $t('studentHome.humanHelp.action')
              : $t('studentHome.humanHelp.comingSoonAction')
          "
          :coming-soon="!teacherExperienceAvailable"
          @click="onTeacherComingSoon"
        />
        <!--
          V405-C (doc 26 §7): the Advisor card stays on Home and raises the
          shared Coming Soon dialog's `advisor` variant. It never navigates;
          the `/student/advisor` route itself is kept, untouched.
        -->
        <home-action-card
          testid="home-card-advisor"
          icon="headset"
          :title="$t('studentHome.advisor.title')"
          :sub="$t('studentHome.advisor.sub')"
          :action-text="$t('studentHome.advisor.action')"
          :coming-soon="true"
          @click="onAdvisor"
        />
      </div>
    </section>

    <section class="student-home-rich__reports">
      <home-action-card
        testid="home-card-reports"
        variant="compact"
        icon="book"
        :to="`/${$i18n.locale}/sessions/history`"
        :title="$t('studentHome.reports.title')"
        :sub="$t('studentHome.reports.sub')"
        :action-text="$t('studentHome.reports.action')"
      />
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AiTutorHero from './AiTutorHero.vue'
import HomeActionCard from './HomeActionCard.vue'

export default {
  name: 'StudentHomeRich',
  components: { AiTutorHero, HomeActionCard },
  computed: {
    ...mapGetters({
      firstName: 'user/firstName',
      // SX-02: the ONE central capability (doc 22 §4); strictly boolean.
      teacherExperienceAvailable:
        'featureFlags/isStudentTeacherExperienceAvailable',
    }),
  },
  methods: {
    onTeacherComingSoon() {
      this.$store.dispatch('app/comingSoon/show', {
        variant: 'teacher',
        reason: 'help',
      })
    },
    onAdvisor() {
      this.$store.dispatch('app/comingSoon/show', { variant: 'advisor' })
    },
  },
}
</script>

<style lang="scss" scoped>
// Pixel values below (font-size/line-height/margin) are measured directly
// from the rendered prototype (00_*.md §12 PF-09 -- D:/tmp/student-foundation
// -design/borna-final-prototype.html, .home-welcome/.human-help-section),
// not from its source. Deliberately not font-category() here: that shared
// scale doesn't offer these exact values, and rendered-prototype parity
// takes precedence over reusing the mixin for this specific region.
$shr-ink: #071447;
$shr-muted: #65718d;

.student-home-rich {
  @include flex-container(column);
  padding: 0;

  // Student Journey V2 `.home-screen`: 8px above the column in the mobile
  // shell, 4px on phones, none on desktop (the page box already gives 34px).
  @include borna-mobile {
    padding-top: 8px;
  }

  @include borna-phone {
    padding-top: 4px;
  }
}

.student-home-rich__welcome {
  margin: 4px 0 18px;

  // Student Journey V2: clamp() above the phone tier, a flat 21px from it down
  // (re-measured at 1440 / 700 / 390) -- see the token note.
  h1 {
    color: $shr-ink;
    margin: 0;
    font-size: $borna-fs-h1-greeting;
    font-weight: $borna-fw-regular;
    letter-spacing: -0.035em;
    line-height: 1.55;
  }

  p {
    color: $shr-muted;
    margin: 4px 0 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.8;
  }

  @include borna-phone {
    margin: 2px 0 13px;

    h1 {
      font-size: $borna-fs-h1-home-phone;
    }

    p {
      margin-top: 2px;
      font-size: 13px;
    }
  }
}

.student-home-rich__grid {
  margin-top: 27px;

  @include borna-phone {
    margin-top: 21px;
  }
}

.student-home-rich__grid-heading {
  margin-bottom: 13px;

  // WP-04V-E: measured 20px at 1440, 18px at 390.
  h2 {
    color: $shr-ink;
    margin: 0;
    font-size: $borna-fs-h2-section;
    font-weight: $borna-fw-regular;
    line-height: 1.6;
  }

  p {
    color: $shr-muted;
    margin: 2px 0 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.8;
  }

  @include borna-phone {
    margin-bottom: 10px;

    p {
      font-size: 13px;
    }
  }
}

.student-home-rich__grid-cards {
  // The rendered prototype keeps Teacher/Advisor as two columns down to at
  // least a 390px mobile viewport (00_*.md §12 PF-09,
  // reference-home-mobile-fa.png) -- no single-column mobile stacking here.
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  // Student Journey V2: the reference stacks them at <=380px (its
  // `.human-help-grid`). Below that width two columns push the Advisor card's
  // badge outside a 320-375px phone (measured; unchanged since before this
  // package), i.e. horizontal scroll.
  @include borna-narrow {
    grid-template-columns: 1fr;
  }
}

.student-home-rich__reports {
  margin-top: 16px;
}
</style>
